import { getSecurityId } from 'reducers/users';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import API from 'services/api';
import { setLoading, showSnackbar, toggleModal } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import { actions as patrolTemplateFilterActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/filters';
import { nameSpace } from 'containers/Pages/Tasks/PatrolTemplates/Modules/nameSpace';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import { createSiteRequest } from '../sitesSaga/sitesSaga';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { createSimpleTask } from '../templateSaga/templateSaga';
import { convertImageToBase64 } from 'utils/DataTransform/Patrols/Form/PatrolDefObject';

export function* fetchPatrolTemplateListSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { filters, from } = payload;
    const securityCompanyId = yield select(getSecurityId);
    const { data } = yield call(API.fetchPatrolDefList, {
      securityCompanyId,
      from,
      ...filters,
    });
    if (data.data != null) {
      const totalEntries = get(data, 'meta.total');
      yield put(
        patrolTemplateActions.setGridList({ list: data.data, totalEntries }),
      );
    }
    yield put(patrolTemplateFilterActions.setFilterKey(false));
    yield put(patrolTemplateActions.setForceRefresh(false));
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
function calcImageSize(url) {
  if (!url) return { width: 0, height: 0 };
  return new Promise(resolve => {
    const img = new Image();
    img.src = url;
    if (img.complete) {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    } else {
      img.addEventListener('load', () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      });
    }
  });
}

export function* getEvidenceForFirstLocation({ payload }) {
  const { data, patrolEvidenceList } = payload;
  if (data.data != null) {
    if (data.data.firstLocation.taskId) {
      const tasks = yield call(API.fetchTaskDetails, {
        id: data.data.firstLocation.taskId,
      });
      if (patrolEvidenceList) {
        Object.assign(data.data, { patrolEvidenceList: [] });

        data.data.patrolEvidenceList.push(tasks.data.data);
      }
      const subData = tasks.data.data.tasks;
      const taskData = subData.find(id =>
        id === data.data.firstLocation.taskId ? id.evidence : [],
      );
      data.data.firstLocation.evidence = taskData.evidence;
    }
  }
}

export function* getEvidenceForOtherLocations({ payload }) {
  const { data, patrolEvidenceList } = payload;
  if (data.data.locations) {
    for (const key in data.data.locations) {
      if (data.data.locations[key].taskId) {
        const tasks = yield call(API.fetchTaskDetails, {
          id: data.data.locations[key].taskId,
        });

        if (patrolEvidenceList) {
          if (data.data.firstLocation.taskId !== null) {
            data.data.patrolEvidenceList.push(tasks.data.data);
          } else {
            // if nothing is attached as evidence on the first location there will be no taskId;
            Object.assign(data.data, { patrolEvidenceList: [] });
            data.data.patrolEvidenceList.push(tasks.data.data);
          }
        }
        const subData = tasks.data.data.tasks;
        const taskData = subData.find(id =>
          id === data.data.locations[key].taskId ? id.evidence : [],
        );
        data.data.locations[key].evidence = taskData.evidence;
      }
    }
  }
}

export function* fetchPatrolTemplateDetailsSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id, patrolEvidenceList } = payload;

    const { data } = yield call(API.fetchPatrolDefDetails, id);

    // check if data has surfaceId set
    const fetchData = data.data;
    const { width, height } = yield call(
      calcImageSize,
      data.data.surfaces?.filter(f => f.id === data.data?.surfaceId)[0]?.[
        'imageUrl'
      ],
    );

    if (
      data.data &&
      data.data.surfaceId !== undefined &&
      data.data.surfaceId !== null
    ) {
      // extract imageDimensions from the image
      Object.assign(fetchData, {
        imageDimensions: { width, height },
        siteSurfaceImages: data.data.surfaces[0],
      });
    } else {
      // default is geographical maps if patrol is not draw on image
      Object.assign(fetchData, {
        siteSurfaceImages: GEOGRAPHICAL_MAP,
      });
    }
    // store evidence for first location in data
    yield call(getEvidenceForFirstLocation, {
      payload: { data, ...(patrolEvidenceList && { patrolEvidenceList }) },
    });

    // store evidence for all other locations in data
    yield call(getEvidenceForOtherLocations, {
      payload: { data, ...(patrolEvidenceList && { patrolEvidenceList }) },
    });
    // get the list of all the evidences
    const patrolEvidenceTaskList =
      data.data?.patrolEvidenceList &&
      data.data?.patrolEvidenceList.reduce((prev, curr) => {
        prev[curr.tasks[0].id] = curr;
        return prev;
      }, {});
    yield put(
      patrolTemplateActions.setGridRowDetails({
        details: { ...data.data, patrolEvidenceTaskList },
      }),
    );
    yield put(setLoading(false));
  } catch (err) {
    console.log('err: ', err);
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
/* eslint-disable consistent-return */
function* createEmbeddedSite(siteData, patrolPayload) {
  //save any new site first so that we can use the id from the result in the new patrol
  const newSiteResponse = yield call(createSiteRequest, {
    site: { ...siteData },
  });

  if (newSiteResponse.data) {
    return {
      ...patrolPayload,
      businessClient: newSiteResponse.data.businessClient,
      site: {
        id: newSiteResponse.data.id,
        inductionRequired: newSiteResponse.data.inductionRequired,
        name: newSiteResponse.data.name,
      },
    };
  }
}

function* createEvidenceTasks(taskTypeData, locationEvidence, patrolPayload) {
  const patrolCheckPointTaskIDObject = taskTypeData.data.data.find(
    f => f.typeName === serviceTypes.patrolCheckPointTask,
  );
  const tasks = locationEvidence.map(item => ({
    ...item,
    taskTypeID: patrolCheckPointTaskIDObject?.id,
    businessClientId: patrolPayload.businessClient
      ? patrolPayload.businessClient.id
      : undefined,
    siteId: patrolPayload.site ? patrolPayload.site.id : undefined,
  }));
  const { data } = yield call(createSimpleTask, {
    data: { tasks, joins: [], surfaces: [] },
  });
  if (!data) {
    throw data;
  }
}

export function* createPatrolTemplateSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { patrolDef } = payload;
    //Get the task type so that we can save any evidence correctly
    const taskTypeData = yield call(API.fetchTaskTypes, {
      taskCategory: ['PATROL_SINGLE_SITE'],
    });
    const {
      patrolImgData,
      newSite,
      siteData,
      locationEvidence,
      ...patrolData
    } = patrolDef;
    let patrolPayload = { ...patrolData };
    if (newSite) {
      patrolPayload = yield* createEmbeddedSite(siteData, patrolPayload);
    }
    //save the evidence
    if (
      locationEvidence &&
      locationEvidence.length > 0 &&
      taskTypeData?.data.data
    ) {
      /**
       *TODO: [PFM-2885] Moving to version 2.0, make sure this api is stored in redux so that you dont make calls of every update or create.
       */

      //map the taskType ID to taskType
      yield call(
        createEvidenceTasks,
        taskTypeData,
        locationEvidence,
        patrolPayload,
      );
    }
    const imageData = yield call(API.getLocationImage, patrolImgData); //The image will be created by the backend in the future
    if (imageData.data) {
      const imageBase64 = convertImageToBase64(imageData.data, 'binary');
      const patrolDefWithImage = {
        ...patrolPayload,
        imageBase64,
      };
      const { status } = yield call(API.addPatrolDef, patrolDefWithImage);

      if (status === 200) {
        yield put(patrolTemplateFilterActions.setFilterKey(false));
        yield put(patrolTemplateActions.setForceRefresh(false));
        yield put(toggleModal(nameSpace));
      }
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
export function* updatePatrolTemplateSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id, patrolDef } = payload;
    const taskTypeData = yield call(API.fetchTaskTypes, {
      taskCategory: ['PATROL_SINGLE_SITE'],
    });
    const { patrolImgData, locationEvidence, ...patrolData } = patrolDef;

    const patrolPayload = { ...patrolData };
    //save the evidence
    if (
      locationEvidence &&
      locationEvidence.length > 0 &&
      taskTypeData?.data.data
    ) {
      /**
       *TODO: Moving to version 2.0, make sure this api is stored in redux so that you dont make calls of every update or create.
       */

      //map the taskType ID to taskType
      yield call(
        createEvidenceTasks,
        taskTypeData,
        locationEvidence,
        patrolPayload,
      );
    }
    const imageData = yield call(API.getLocationImage, patrolImgData); //The image will be created by the backend in the future
    if (imageData.data) {
      const imageBase64 = convertImageToBase64(imageData.data, 'binary');
      const patrolDefWithImage = {
        ...patrolPayload,
        imageBase64,
      };
      const { status } = yield call(
        API.updatePatrolDef,
        id,
        patrolDefWithImage,
      );
      if (status === 200) {
        yield put(toggleModal(nameSpace));
        yield call(fetchPatrolTemplateDetailsSaga, { payload: { id } });
      }
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
  yield put(setLoading(false));
}
export function* getPatrolQrSaga({ payload }) {
  try {
    const { id } = payload;
    const { data } = yield call(API.getPatrolQr, id);
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchGridRowDetailsPdfSaga({ payload }) {
  try {
    yield call(getPatrolQrSaga, { payload: { id: payload.id } });
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* patrolTemplateSagas() {
  yield takeLatest(
    patrolTemplateActions.fetchGridList,
    fetchPatrolTemplateListSaga,
  );
  yield takeLatest(
    patrolTemplateActions.fetchGridRowDetails,
    fetchPatrolTemplateDetailsSaga,
  );
  yield takeLatest(
    patrolTemplateActions.createRowEntry,
    createPatrolTemplateSaga,
  );
  yield takeLatest(
    patrolTemplateActions.updateRowEntry,
    updatePatrolTemplateSaga,
  );
  yield takeLatest(patrolTemplateActions.fetchQr, getPatrolQrSaga);
  yield takeLatest(
    patrolTemplateActions.fetchGridRowDetailsPdf,
    fetchGridRowDetailsPdfSaga,
  );
}
